<template>
  <!-- Above the Fold: Booking Calendar Section -->
  <div class="container mt-4">
    <div class="grid">
      <div class="col-12 text-center">
        <h2 class="text-primary">Schedule Your Tour Today!</h2>
        <p class="text-xl">Choose a time that works for you and see our beautiful townhomes in person</p>
      </div>
      <div class="col-12">
        <div
          class="calendly-inline-widget"
          data-url="https://calendly.com/molly-maxxpm/dublin-farms-eagle-mountain?primary_color=11475b"
        ></div>
      </div>
    </div>
  </div>

  <!-- Quick Property Overview -->
  <div class="grid container">
    <div class="col-12 text-center mb-4">
      <h2 class="text-primary">Why Choose Dublin Farms?</h2>
    </div>
    <div class="col-12 md:col-4 text-center mb-4">
      <i
        class="pi pi-home text-primary"
        style="font-size: 3rem"
      ></i>
      <h3 class="mt-3">Beautiful Townhomes</h3>
      <p>2 & 3 story townhomes with modern finishes and open concept living</p>
    </div>
    <div class="col-12 md:col-4 text-center mb-4">
      <i
        class="pi pi-map-marker text-primary"
        style="font-size: 3rem"
      ></i>
      <h3 class="mt-3">Prime Location</h3>
      <p>Minutes from shopping, dining, and recreation in Eagle Mountain</p>
    </div>
    <div class="col-12 md:col-4 text-center mb-4">
      <i
        class="pi pi-star text-primary"
        style="font-size: 3rem"
      ></i>
      <h3 class="mt-3">Premium Amenities</h3>
      <p>Fitness center, clubhouse, and beautiful grounds</p>
    </div>
  </div>

  <!-- Property Images Row -->
  <div class="grid grid-nogutter mt-4">
    <div
      v-for="image in propertyImages"
      :key="image.src"
      class="p-0 col-12 md:col-4"
    >
      <DeferredContent>
        <img
          :src="image.src"
          :alt="image.alt"
          class="img-row"
        />
      </DeferredContent>
    </div>
  </div>

  <!-- Apply Now Section -->
  <div class="container mt-5">
    <div class="grid">
      <div class="col-12 text-center">
        <h2 class="text-primary">Ready to Apply?</h2>
        <p class="text-xl mb-4">Start your application process today and secure your new home</p>
        <a
          href="https://maxxpm.appfolio.com/listings/rental_applications/new?listable_uid=381aaf09-63e9-48ec-8973-f20023196312&source=dublinfarms-fb"
          target="_blank"
        >
          <Button
            label="Apply Now"
            icon="pi pi-check-circle"
            rounded
            severity="primary"
            size="large"
          />
        </a>
      </div>
    </div>
  </div>

  <!-- Contact Information -->
  <div class="container mt-5 mb-5">
    <div class="grid">
      <div class="col-12 md:col-4 text-center">
        <i
          class="pi pi-phone text-primary"
          style="font-size: 2.5rem"
        ></i>
        <h3 class="mt-3">Call Us</h3>
        <p>{{ $maxxpm.config.footer.phone }}</p>
      </div>
      <div class="col-12 md:col-4 text-center">
        <i
          class="pi pi-envelope text-primary"
          style="font-size: 2.5rem"
        ></i>
        <h3 class="mt-3">Email Us</h3>
        <p>info@maxxpm.com</p>
      </div>
      <div class="col-12 md:col-4 text-center">
        <i
          class="pi pi-map-marker text-primary"
          style="font-size: 2.5rem"
        ></i>
        <h3 class="mt-3">Visit Us</h3>
        <p>{{ $maxxpm.config.footer.address }}<br />{{ $maxxpm.config.footer.cityStateZip }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, onMounted, onUnmounted } from "vue";
import { event as gtagEvent } from 'vue-gtag'
import { useMetaPixel } from "@/composables/useMetaPixel";
import DeferredContent from "primevue/deferredcontent";
import Button from "primevue/button";

const $maxxpm = inject("$maxxpm");
const { trackCustomEvent, trackSchedule } = useMetaPixel();
const propertyImages = [
  {
    src: "https://cdn.maxxpm.com/dublinfarms/2storysteve/staged/1.jpg",
    alt: "Dublin Farms Interior Photo",
  },
  {
    src: "https://cdn.maxxpm.com/dublinfarms/2storysteve/staged/7.jpg",
    alt: "Modern kitchen and dining area",
  },
  {
    src: "https://cdn.maxxpm.com/dublinfarms/2storysteve/staged/2.jpg",
    alt: "Spacious bedroom",
  },
];

const widgetHeight = ref("700px");

const handleMessage = (event) => {
  if (event.origin === "https://calendly.com") {
    const data = event.data;
    if (data ) {
      switch (data.event) {
        case "calendly.page_height":
          widgetHeight.value = data.payload.height;
          break;
        case 'calendly.date_and_time_selected':
          gtagEvent('agent_showing_date_time_selected', {
            event_category: 'tenants',
            event_label: 'facebook_ad',
          })
          trackCustomEvent('agent_showing_date_time_selected')
          break;
        case 'calendly.event_scheduled':
          gtagEvent('agent_showing_booked', {
            event_category: 'tenants',
            event_label: 'facebook_ad',
          })
          trackSchedule()
          break;
      }
    }
  }
};

onMounted(() => {
  const script = document.createElement("script");
  script.src = "https://assets.calendly.com/assets/external/widget.js";
  script.async = true;
  document.body.appendChild(script);

  window.addEventListener("message", handleMessage);
});

onUnmounted(() => {
  window.removeEventListener("message", handleMessage);
});
</script>

<style scoped>
.calendly-inline-widget {
  min-width: 320px;
  height: v-bind(widgetHeight);
  width: 100%;
}

.container {
  width: 95vw;
  max-width: 1200px;
  margin: auto;
}

.img-row {
  width: 100%;
  height: 380px;
  object-fit: cover !important;
}
</style>
